import { FunctionComponent } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import EmptyStateAction, { EmptyAction } from './EmptyStateAction'
import { Info, User } from 'react-feather'
import colors from '../../colors'
import { SPACING } from '../../constants'
import { RecentMember } from '../../types'

type RecentMembersCardProps = {
  emptyAction?: EmptyAction,
  members: RecentMember[],
  loading: boolean,
}

const RecentMembersCard: FunctionComponent<RecentMembersCardProps> = ({ members, loading, emptyAction }) => {
  const styles = useStyles()
  console.log(members)

  const InfoIcon: FunctionComponent = () => {
    return (
      <Box className={styles.infoContainer}>
        <Tooltip title="Your most recent members who joined in the last 30 days">
          <Info size={16} strokeWidth={3} color={colors.GREY_3} />
        </Tooltip>
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleContainer}>
        <User size={20} color={colors.GREY_3} style={{ marginRight: SPACING }} />
        <Typography variant="h6">
          Recent Members
        </Typography>
        <InfoIcon />
      </Box>

      {loading ? (
        <Box mt={2}>
          <Skeleton count={5} height={40} />
        </Box>
      ) : members.length === 0 ? (
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No members yet.
          </Typography>
          <EmptyStateAction action={emptyAction} />
        </Box>
      ) : (
        <Box className={styles.tableScroll}>
  <Table size="small">
            <TableHead>
              <TableRow>
                {/* Email and membership are deliberately absent: this card
                    sits in a third-width column, and five columns there
                    scrolled inside the card. Both are on the Patrons page,
                    where there is room to read them. */}
                <TableCell className={styles.tableHeader}>Name</TableCell>
                <TableCell className={styles.tableHeader}>Joined</TableCell>
                <TableCell className={styles.tableHeader} align="right">MRR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id} hover className={styles.tableRow}>
                  <TableCell>{member.patronName || '-'}</TableCell>
                  <TableCell>{member.joinedDate}</TableCell>
                  <TableCell align="right" className={styles.mrrCell}>{member.mrr}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  // The card must never widen the page: a table has an intrinsic minimum
  // width, and in a third-width column it exceeded the card and pushed the
  // whole dashboard into horizontal scroll.
  tableScroll: {
    width: '100%',
    overflowX: 'auto',
  },
  container: {
    borderRadius: 12,
    boxShadow: '0px 0px 4px rgba(17, 51, 85, 0.15)',
    padding: SPACING * 3,
    backgroundColor: '#fff',
    width: '100%',
    minWidth: 0,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: SPACING * 2,
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    cursor: 'pointer',
  },
  tableHeader: {
    fontWeight: 600,
    color: colors.GREY_3,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  mrrCell: {
    fontWeight: 600,
    color: '#2e7d32',
  },
  emptyState: {
    padding: SPACING * 4,
    display: 'flex',
    // Column, so an empty-state action sits under the message rather than
    // running on from the end of the sentence.
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    textAlign: 'center',
  },
})

export default RecentMembersCard
